import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Factory, Zap, Filter, X } from "lucide-react";
import { api } from "@/lib/api";
import SEO, { collectionSchema } from "@/components/SEO";

const CATEGORY_ACCENT = {
  "Precious Metal": "bb-tag-neutral",
  "Base Metal": "bb-tag-neutral",
  "Battery Metal": "bb-tag-positive",
  "Energy Metal": "bb-tag-positive",
  "Bulk Commodity": "bb-tag-neutral",
  "Industrial Mineral": "bb-tag-neutral",
};

export default function CommoditiesListPage() {
  const [commodities, setCommodities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criticalOnly, setCriticalOnly] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/commodities"), api.get("/projects")])
      .then(([c, p]) => {
        if (!mounted) return;
        if (Array.isArray(c.data)) setCommodities(c.data);
        if (Array.isArray(p.data)) setProjects(p.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const projectCount = useMemo(() => {
    const map = {};
    projects.forEach((p) =>
      (p.commodity || []).forEach((c) => {
        map[c] = (map[c] || 0) + 1;
      }),
    );
    return map;
  }, [projects]);

  const filtered = criticalOnly
    ? commodities.filter((c) => c.criticalMineral)
    : commodities;

  return (
    <div data-testid="commodities-list-page">
      <SEO
        title="Commodities"
        description="Daily commodity coverage with a Canadian lens — precious & base metals, battery inputs, energy metals, and critical minerals."
        jsonLd={collectionSchema("Commodities", "Canadian mining commodities coverage", "/commodities")}
      />
      <section className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="bb-overline text-bb-copper">
                Markets · Coverage
              </div>
              <h1
                className="bb-headline mt-4 text-4xl sm:text-5xl lg:text-6xl text-bb-navy"
                data-testid="commodities-page-headline"
              >
                Commodities
              </h1>
              <p className="mt-5 text-base lg:text-lg text-bb-slate max-w-2xl leading-relaxed">
                Daily commodity coverage with a Canadian lens — precious &
                base metals, battery inputs, energy metals, bulk commodities,
                and industrial minerals. Critical-minerals designations noted
                where applicable.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="bb-overline text-bb-slate">
                {filtered.length} covered
              </span>
              <button
                type="button"
                onClick={() => setCriticalOnly((v) => !v)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-archivo uppercase tracking-widest border transition-colors ${
                  criticalOnly
                    ? "bg-bb-copper text-white border-bb-copper"
                    : "bg-white text-bb-navy border-border hover:border-bb-navy"
                }`}
                data-testid="commodities-critical-filter"
              >
                {criticalOnly ? <X size={12} /> : <Filter size={12} />}
                Critical minerals
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="bb-container">
          {loading ? (
            <div className="text-center py-20 bb-overline text-bb-slate">
              Loading commodities…
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="commodities-grid"
            >
              {filtered.map((c) => (
                <Link
                  key={c.id}
                  to={`/commodities/${c.slug}`}
                  className="bb-card p-6 flex flex-col gap-3 group"
                  data-testid={`commodity-card-${c.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-bb-offwhite text-bb-navy border border-border">
                      <Factory size={18} strokeWidth={1.8} />
                    </span>
                    {c.criticalMineral && (
                      <span
                        className="inline-flex items-center gap-1 bb-tag-negative text-[10px] font-mono px-2 py-1"
                        data-testid={`commodity-card-${c.slug}-critical-badge`}
                      >
                        <Zap size={10} strokeWidth={2.5} />
                        Critical
                      </span>
                    )}
                  </div>
                  <div>
                    <span
                      className={`text-[10px] font-mono px-2 py-0.5 ${CATEGORY_ACCENT[c.category] || "bb-tag-neutral"}`}
                    >
                      {c.category}
                    </span>
                  </div>
                  <h3 className="font-archivo text-xl text-bb-navy font-semibold tracking-tight leading-tight">
                    <span className="bb-link">{c.name}</span>
                  </h3>
                  <p className="text-sm text-bb-slate leading-relaxed line-clamp-3">
                    {c.canadianContext}
                  </p>
                  <div className="mt-auto pt-4 border-t border-border flex items-center justify-between">
                    <span className="text-[11px] font-mono text-bb-slate">
                      {projectCount[c.name] || 0} project
                      {(projectCount[c.name] || 0) === 1 ? "" : "s"} tracked
                    </span>
                    <ArrowUpRight
                      size={14}
                      className="text-bb-navy group-hover:text-bb-copper transition-colors"
                    />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
